import { EventEmitter, Injectable } from "@angular/core";
import { EntityService } from "../../entity.service";
import { Entity, Plan } from "../../../model/entity";
import {
  Reservation,
  ReservationCalendar,
  ReservationEquipmentAvailability,
  ReservationExtraordinaryCalendar,
} from "../../../model/reservation";
import { PracticeService } from "../../practice.service";
import { Observable } from "rxjs";
import {
  PracticeRegionReservation,
  PracticeRegionSearchReservation,
} from "../../../model/practice-region-reservation";
import { SetSuppliesAditionalReservation } from "../../../model/set-supplies-aditional-reservation";
import { ServiceReservation } from "../../../model/service-reservation";
import { PatientSearch } from "../../../model/patient/patient-search";
import { Center } from "../../../model/center";
import { ValorizationService } from "../../valorization/valorization.service";
import { ReservationService } from "../../reservation.service";
import { Question } from "../../../model/question";
import { ReservationSlot } from "../../../model/reservation-slot";
import { PracticeRegion } from "../../../model/practice-region";
import { ReservationQuestion } from "../../../model/reservation-question";
import { Reminder } from "../../../model/reminder";
import { Router } from "@angular/router";
import { MedicalAppointmentCancel } from "../../../model/medical-appointment-cancel";
import { KeyedCollection } from "../../../infrastructure/keyed-collection";
import { ReservationRescheduleWizardService } from "./reservation-reschedule.wizard.service";
import { AuthService } from "../../auth.service";
import { Answers } from "../../../model/answers";
import { ConfirmDialogService } from "../../confirm-dialog.service";
import { NotificationToastService } from "../../notification-toast.service";
import { SlotValidationResponse } from "../../../model/slot-validation-response";
import { PracticePriceReservationResponse } from "../../../model/practice-price-reservation-response";
import { OnLineValidationService } from "../../on-line-validation/on-line-validation.service";
import { MedicalAppointmentValidation } from "../../../model/medical-appointment-validation";
import { EquipmentFilter } from "../../../model/equipment-filter";
import { EnvironmentPresetService } from "../../environment-preset/environment-preset.service";
import { IntegratedAdmissionService } from "../../integrated-admission/integrated-admission.service";

@Injectable()
export abstract class ReservationWizardService {
  public onCancelWizard = new EventEmitter();
  public reservationHisId: number | null;
  public returnNavigate = "";
  public isRescheduling: boolean;
  public firstNameSearh = "";
  public lastNameSearh = "";
  public documentSearh = "";
  public reservationQuestion: ReservationQuestion;
  public reservation: Reservation;
  public reminders: Reminder[] = [];
  public reservationFinished = false;
  public reservationExtraordinaryCalendar: ReservationExtraordinaryCalendar;
  public planPracticesRegionSearch: KeyedCollection<
    PracticeRegionSearchReservation[]
  >;


  constructor(
    public router: Router,
    public entityService: EntityService,
    public valorizationService: ValorizationService,
    public authService: AuthService,
    public reservationService: ReservationService,
    public confirmDialogService: ConfirmDialogService,
    public reservationRescheduleWizardService: ReservationRescheduleWizardService,
    public notificationToastService: NotificationToastService,
    public onLineValidationService: OnLineValidationService,
    public integratedAdmissionService: IntegratedAdmissionService,
    public environmentPresetService: EnvironmentPresetService,
    public practiceService: PracticeService,
  ) {
    this.reset();
  }

  public navigateToReservationWizard(): void {
    this.router.navigate(["reservation-wizard"]);
  }

  reset(): void {
    this.planPracticesRegionSearch = new KeyedCollection<
      PracticeRegionSearchReservation[]
    >();
    this.reservationHisId = null;
    this.isRescheduling = false;
    this.firstNameSearh = "";
    this.lastNameSearh = "";
    this.documentSearh = "";
    this.returnNavigate = "";
    this.reservation = new Reservation();
    this.reservation.MedicalOrderScanns = [];
    this.reservationQuestion = new ReservationQuestion();
    this.reservation.ServiceReservations = [];
    this.reservationFinished = false;
    const serviceReservation = new ServiceReservation();
    this.reservation.Answers = [];
    this.reservationExtraordinaryCalendar = null;
    this.reservation.Sessions = 1;
    this.reservation.ServiceReservations.push(serviceReservation);
  }

  abstract setReservationExtraordinaryCalendar(
    reservationExtraordinaryCalendar: ReservationExtraordinaryCalendar,
  );
  abstract getPatient(): PatientSearch;
  abstract setReservationDate(date: Date);
  abstract setReservationSlot(reservationSlot: ReservationSlot): void;
  abstract selectedSlotCount(): number;
  abstract clearReservationSlot(reservationSlot: ReservationSlot): void;
  abstract clearAllReservationSlot(): void;
  abstract existsReservationServiceWithOutSlot(): boolean;
  abstract isValidReservationSlots(): boolean;
  abstract validationSlotMessage(): string;
  abstract authorizationRequired(): boolean;
  abstract getReservationSlotToAuthorize(): ReservationSlot;
  abstract removeServiceReservation(
    serviceReservation: ServiceReservation,
  ): void;
  abstract getServiceReservation(index: number): ServiceReservation;
  abstract getNewServiceReservation(): ServiceReservation;
  abstract getPracticesRegionSearchReservation(
    planId: number,
    excludedPracticesRegionIds: number[],
    serviceId?: number,
    centerId?: number,
  ): Promise<PracticeRegionSearchReservation[]>;
  abstract getPracticeRegionReservation(
    planId: number,
    name: string,
    excludePracticesIds: number[],
    principalPracticeRegionId?: number,
    centerId?: number,
  ): Observable<PracticeRegionReservation[]>;
  abstract clearQuestionsAndSlotReservation(): void;
  abstract get isQuestionVisible(): boolean;
  abstract get getAllPrincipalPracticeRegion(): PracticeRegion[];
  abstract get getAllServiceReservations(): ServiceReservation[];
  abstract get allPracticesIds(): number[];
  abstract get allPrincipalPracticesIds(): number[];
  abstract get allPracticesAndAditionalIds(): number[];
  abstract setServiceReservationPracticeInfo(
    serviceReservation: ServiceReservation,
  );
  abstract setServiceReservationOrderInfo(
    serviceReservation: ServiceReservation,
  );
  abstract practicePriceReservation(): Promise<PracticePriceReservationResponse>;
  abstract loadReservationQuestions();
  abstract getEntities(): Promise<Entity[]>;
  abstract getPlansByEntityId(entityId: number): Promise<Plan[]>;
  abstract getAditionalPracticeAndSetSuppliesReservation(
    practiceRegionIds: number[],
    planId: number,
    centerId?: number,
  ): Promise<SetSuppliesAditionalReservation[]>;
  abstract reservationCalendar(): Promise<ReservationCalendar>;
  abstract setSeeDraft(seeDraft: boolean): void;
  abstract setSeeNoRulesRestriction(seeNoRules: boolean): void;
  abstract setSeeNoRules(seeNoRules: boolean): void;
  abstract completePatientInfo(reservationSlots: ReservationSlot[]);
  abstract finalizeReservation();
  abstract sendResevationMail(): Promise<void>;
  abstract durationExtraordinaryReservation(
    equipmentId: number,
    physicianId: number,
  ): Promise<number>;
  abstract cancelByRescheduleMedicalAppointment();
  abstract reschedule();
  abstract rescheduleDos(reservation: Reservation, returnNavigate: string);
  abstract reAppointment(reservation: Reservation, returnNavigate: string);
  abstract EnableCombo(): boolean;
  abstract validateSlotReservation(): void;

  getSessions(): number {
    return this.reservation.Sessions;
  }

  setSessions(sessions: number): void {
    this.reservation.Sessions = sessions;
    this.validateSlotReservation();
  }

  getReservationEquipmentAvailability(): Promise<ReservationEquipmentAvailability> {
    return this.reservationService.getReservationEquipmentAvailability(
      this.reservation,
    );
  }

  forceReservationEquipment(equipment: EquipmentFilter): void {
    this.reservation.ServiceReservations.forEach((r) => {
      r.ReservationSlot.EquipmentId = equipment.Id;
      r.ReservationSlot.EquipmentName = equipment.Name;
    });
  }

  async executePreAuthorization(): Promise<MedicalAppointmentValidation[]> {
    const response: MedicalAppointmentValidation[] = [];

    for (let i = 0; i < this.reservation.ServiceReservations.length; i++) {
      const item = this.reservation.ServiceReservations[i];
      const validation =
        await this.onLineValidationService.executePreAuthorization(
          item,
          this.reservation.Patient.Id,
        );
      item.MedicalAppointmentValidation = validation;
      response.push(validation);
    }

    return response;
  }

  async executeOnLineOperationInReservationPractice(entity: Entity , plan: Plan): Promise<MedicalAppointmentValidation> {

       if (!this.reservation || !this.reservation.Patient || !this.reservation.Patient.Id) {
         throw new Error("Debe seleccionar un paciente para validar la práctica.");
       }

       const item : ServiceReservation = new ServiceReservation();
       item.Plan = plan;
       item.Entity = entity;

       return await this.onLineValidationService.executeOnLineOperationInReservation(
          item,
          this.reservation.Patient.Id,
        );

  }


  async executeOnLineOperation(): Promise<MedicalAppointmentValidation[]> {
    const response: MedicalAppointmentValidation[] = [];

    for (let i = 0; i < this.reservation.ServiceReservations.length; i++) {
      const item = this.reservation.ServiceReservations[i];

      const validation =
        await this.onLineValidationService.executeOnLineOperationInReservation(
          item,
          this.reservation.Patient.Id,
        );
      item.MedicalAppointmentValidation = validation;
      response.push(validation);
    }

    return response;
  }

  getEntityPreAuthorizeInReservation(entityId: number): Promise<boolean> {
    return this.entityService.getEntityPreAuthorizeInReservation(entityId);
  }

  setPatient(patient: PatientSearch): void {
    this.reservation.Patient = patient;
  }

  async createPendingReservation(): Promise<boolean> {
    return await this.reservationService.createPendingReservation(
      this.reservation,
    );
  }

  getValidateDateMessage(): string {
    let msg = "";
    this.reservation.ServiceReservations.forEach((serviceReservation) => {
      if (!serviceReservation.isValidaDate()) {
        msg = serviceReservation.isAthorizationValidity()
          ? "Debe revalidar la fecha de la autorización"
          : "Debe revalidar la fecha de la orden";
      }
    });
    return msg;
  }

  hasDate(): boolean {
    let hasDate = false;
    this.reservation.ServiceReservations.forEach((serviceReservation) => {
      if (!hasDate) {
        hasDate = serviceReservation.hasDate();
      }
    });
    return hasDate;
  }

  cancel(): void {
    this.onCancelWizard.emit();
  }

  getIsInternation(): boolean {
    return this.reservation.IsInternation;
  }

  setIsInternation(isInternation: boolean): void {
    this.reservation.IsInternation = isInternation;
  }

  finalize(): void {
    const retNavigate = this.returnNavigate;
    this.reset();
    if (retNavigate !== "") {
      this.router.navigate([retNavigate]);
    } else {
      this.navigateToReservationWizard();
    }
  }

  setAnswers(answers: Answers[], questions: Question[]): void {
    this.reservation.Answers = answers;
    this.reservation.Questions = questions;
  }
}

@Injectable()
export class ReservationRegularWizardService extends ReservationWizardService {
  slotValidationResponse: SlotValidationResponse;

  setSeeDraft(seeDraft: boolean): void {
    this.reservation.SeeDraft = seeDraft;
  }

  setSeeNoRulesRestriction(seeNoRule: boolean): void {
    this.reservation.SeeNoRulesRestriction = seeNoRule;
  }

  setSeeNoRules(seeNoRules: boolean): void {
    this.reservation.SeeNoRules = seeNoRules;
  }

  EnableCombo(): boolean {
    return true;
  }

  setReservationExtraordinaryCalendar(
    reservationExtraordinaryCalendar: ReservationExtraordinaryCalendar,
  ) {
    throw new Error("Method not implemented.");
  }

  durationExtraordinaryReservation(
    equipmentId: number,
    physicianId: number,
  ): Promise<number> {
    throw new Error("Method not implemented.");
  }

  validationSlotMessage(): string {
    if (this.slotValidationResponse) {
      return this.slotValidationResponse.Message;
    } else {
      return "";
    }
  }

  isValidReservationSlots(): boolean {
    if (this.slotValidationResponse) {
      return this.slotValidationResponse.IsOk;
    } else {
      return false;
    }
  }

  getPatient(): PatientSearch {
    return this.reservation.Patient;
  }

  setReservationDate(date: Date) {
    this.reservation.SelectedDate = date;
  }

  setReservationSlot(reservationSlot: ReservationSlot): void {
    const isCombo = this.getAllPrincipalPracticeRegion.length > 1;
    this.slotValidationResponse = null;

    const serviceReservation = isCombo
      ? this.getAllServiceReservations.find(
          (s) =>
            s.PracticesRegionReservation[0].Id ===
            reservationSlot.PracticeRegionId,
        )
      : this.getAllServiceReservations.find(
          (s) =>
            s.PracticesRegionReservation[0].ServiceId ===
            reservationSlot.ServiceId,
        );

    serviceReservation.ReservationSlot = reservationSlot;

    this.validateSlotReservation();
  }

  validateSlotReservation(): void {
    if (!this.existsReservationServiceWithOutSlot()) {
      this.reservationService
        .validateSlotReservation(this.reservation)
        .then((response) => {
          this.slotValidationResponse = response;
        })
        .catch((err) =>
          this.notificationToastService.error(
            JSON.stringify(err.error),
            "Reserva",
          ),
        );
    }
  }

  clearReservationSlot(reservationSlot: ReservationSlot): void {
    const isCombo = this.getAllPrincipalPracticeRegion.length > 1;
    const serviceReservation = isCombo
      ? this.getAllServiceReservations.find(
          (s) =>
            s.PracticesRegionReservation[0].Id ===
            reservationSlot.PracticeRegionId,
        )
      : this.getAllServiceReservations.find(
          (s) =>
            s.PracticesRegionReservation[0].ServiceId ===
            reservationSlot.ServiceId,
        );

    if (serviceReservation) {
      serviceReservation.ReservationSlot = null;
    }
  }

  selectedSlotCount(): number {
    return this.getAllServiceReservations.filter(
      (s) => s.ReservationSlot != null,
    ).length;
  }

  clearAllReservationSlot(): void {
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      item.ReservationSlot = null;
    });
  }

  existsReservationServiceWithOutSlot(): boolean {
    return (
      this.getAllServiceReservations.length === 0 ||
      this.getAllServiceReservations.findIndex(
        (s) => s.ReservationSlot == null,
      ) > -1
    );
  }

  authorizationRequired(): boolean {
    return (
      this.getAllServiceReservations.findIndex(
        (s) =>
          !s.ReservationSlot.Authorized &&
          s.ReservationSlot.MedicalAppointmentId > 0,
      ) > -1
    );
  }

  getReservationSlotToAuthorize(): ReservationSlot {
    const res = this.getAllServiceReservations.find(
      (s) =>
        !s.ReservationSlot.Authorized &&
        s.ReservationSlot.MedicalAppointmentId > 0,
    )[0];
    return res;
  }

  removeServiceReservation(serviceReservation: ServiceReservation): void {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations.splice(index, 1);
  }

  getServiceReservation(index: number): ServiceReservation {
    return this.reservation.ServiceReservations.find((x) => x.Index === index);
  }

  getNewServiceReservation(): ServiceReservation {
    let plan: Plan;
    let entity: Entity;
    let center: Center;

    let serviceReservation: ServiceReservation = null;
    let index = 0;

    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      plan = item.Plan;
      entity = item.Entity;
      center = item.Center;

      if (item.Index > index) {
        index = item.Index;
      }

      if (
        serviceReservation == null &&
        item.principalPracticeRegionReservationName() === ""
      ) {
        serviceReservation = item;
      }
    });

    if (serviceReservation == null) {
      serviceReservation = new ServiceReservation();
      serviceReservation.Entity = entity;
      serviceReservation.Plan = plan;
      serviceReservation.Center = center;
      serviceReservation.Index = index + 1;
      this.reservation.ServiceReservations.push(serviceReservation);
    }

    return serviceReservation;
  }

  async getPracticesRegionSearchReservation(
    planId: number,
    excludedPracticesRegionIds: number[],
    serviceId?: number,
    centerId?: number,
  ): Promise<PracticeRegionSearchReservation[]> {
    const response: PracticeRegionSearchReservation[] = [];
    let items: PracticeRegionSearchReservation[] = [];

    if (!this.planPracticesRegionSearch.ContainsKey(planId.toString())) {
      this.planPracticesRegionSearch.Add(
        planId.toString(),
        await this.practiceService.getPracticesRegionSearchReservation(
          planId,
          null,
          centerId,
        ),
      );
    }

    items = this.planPracticesRegionSearch.Item(planId.toString());
    items.forEach((item: PracticeRegionSearchReservation) => {
      if (
        (serviceId == null || serviceId === item.ServiceId) &&
        excludedPracticesRegionIds.indexOf(item.PracticeRegionId) === -1
      ) {
        response.push(item);
      }
    });

    return response;
  }

  getPracticeRegionReservation(
    planId: number,
    name: string,
    excludePracticesIds: number[],
    principalPracticeRegionId?: number,
    centerId?: number,
  ): Observable<PracticeRegionReservation[]> {
    return this.practiceService.getPracticeRegionReservation(
      planId,
      name,
      excludePracticesIds,
      principalPracticeRegionId,
      centerId,
    );
  }

  clearQuestionsAndSlotReservation(): void {
    if (!this.reservationQuestion) {
      this.reservationQuestion = new ReservationQuestion();
    }
    this.reservationQuestion.QuestionsFinish = false;
    this.reservation.Answers = [];
    this.clearAllReservationSlot();
  }

  get isQuestionVisible(): boolean {
    return (
      this.reservationQuestion && this.reservationQuestion.Questions.length > 0
    );
  }

  get getAllPrincipalPracticeRegion(): PracticeRegion[] {
    const practicesRegion: PracticeRegion[] = [];
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      practicesRegion.push(item.PracticesRegionReservation[0].PracticeRegion);
    });
    return practicesRegion;
  }

  get getAllServiceReservations(): ServiceReservation[] {
    return this.reservation.getAllServiceReservations();
  }

  get allPracticesIds(): number[] {
    const ids: number[] = [];
    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      if (item.allPracticesRegionIds().length > 0) {
        item.allPracticesRegionIds().forEach((id: number) => {
          ids.push(id);
        });
      }
    });
    return ids;
  }

  get allPrincipalPracticesIds(): number[] {
    const ids: number[] = [];
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      ids.push(item.PracticesRegionReservation[0].Id);
    });
    return ids;
  }

  get allPracticesAndAditionalIds(): number[] {
    const ids: number[] = [];
    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      if (item.allPracticesAndAditionalRegionIds().length > 0) {
        item.allPracticesAndAditionalRegionIds().forEach((id: number) => {
          ids.push(id);
        });
      }
    });
    return ids;
  }

  async setServiceReservationPracticeInfo(
    serviceReservation: ServiceReservation,
  ) {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations[index].Center =
      serviceReservation.Center;
    this.reservation.ServiceReservations[index].Entity =
      serviceReservation.Entity;
    this.reservation.ServiceReservations[index].Plan = serviceReservation.Plan;
    this.reservation.ServiceReservations[index].PracticesRegionReservation =
      serviceReservation.PracticesRegionReservation;
    this.reservation.ServiceReservations[
      index
    ].PracticesRegionAditionalReservation =
      serviceReservation.PracticesRegionAditionalReservation;
    this.reservation.ServiceReservations[index].PracticeRegionReservation =
      serviceReservation.PracticeRegionReservation;
    this.reservation.ServiceReservations[index].SetSuppliesReservation =
      serviceReservation.SetSuppliesReservation;
  }

  async setServiceReservationOrderInfo(serviceReservation: ServiceReservation) {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations[index].DateOrder =
      serviceReservation.DateOrder;
    this.reservation.ServiceReservations[index].DateAuthorization =
      serviceReservation.DateAuthorization;
    this.reservation.ServiceReservations[index].Diagnostic =
      serviceReservation.Diagnostic;
    this.reservation.ServiceReservations[index].ReferringPhysician =
      serviceReservation.ReferringPhysician;
    this.reservation.ServiceReservations[index].Observation =
      serviceReservation.Observation;
    this.reservation.ServiceReservations[index].PatientObservation =
      serviceReservation.PatientObservation;
    this.reservation.ServiceReservations[index].CredentialNumber =
      serviceReservation.CredentialNumber;
    this.reservation.ServiceReservations[index].SecurityCodeCredentialNumber =
      serviceReservation.SecurityCodeCredentialNumber;
  }

  async practicePriceReservation(): Promise<PracticePriceReservationResponse> {
    return await this.valorizationService.practicePriceReservation(
      this.reservation,
    );
  }

  async loadReservationQuestions() {
    if (this.allPracticesAndAditionalIds.length > 0) {
      this.reservationQuestion = new ReservationQuestion();
      const patientId = this.getPatient() ? this.getPatient().Id : null;
      const info = await this.reservationService.getReservationQuestions(
        this.allPracticesAndAditionalIds,
        patientId,
        this.reservation.MedicalAppointmentIdsToCancel,
      );
      this.reservationQuestion.Answers = info.Answers;
      this.reservationQuestion.Questions = info.Questions;
    }
  }

  async getEntities(): Promise<Entity[]> {
    return await this.entityService.getEntities(true);
  }

  async getPlansByEntityId(entityId: number): Promise<Plan[]> {
    return await this.entityService.getPlansByEntityId(entityId);
  }

  async getAditionalPracticeAndSetSuppliesReservation(
    practiceRegionIds: number[],
    planId: number,
    centerId?: number,
  ): Promise<SetSuppliesAditionalReservation[]> {
    return await this.practiceService.getAditionalPracticeAndSetSuppliesReservation(
      practiceRegionIds,
      planId,
      centerId,
    );
  }

  async reservationCalendar(): Promise<ReservationCalendar> {
    return await this.reservationService.reservationRisCalendar(
      this.reservation,
    );
  }

  async completePatientInfo(reservationSlots: ReservationSlot[]) {
    const completeDateSlots =
      await this.reservationService.completePatientInfo(reservationSlots);
    reservationSlots.forEach((slot) => {
      const completeSlot = completeDateSlots.find(
        (cslot) => cslot.MedicalAppointmentId === slot.MedicalAppointmentId,
      );
      if (completeSlot) {
        slot.Patient =
          completeSlot.PatientLastName + ", " + completeSlot.PatientFirstName;
        slot.PatientLastName = completeSlot.PatientLastName;
        slot.PatientFirstName = completeSlot.PatientFirstName;
        slot.PracticeRegionId = completeSlot.PracticeRegionId;
        slot.PracticeRegionName = completeSlot.PracticeRegionName;
        slot.PatientId = completeSlot.PatientId;
      }
    });
  }

  async finalizeReservation() {
    let notified = false;
    const canViewMedicaOrderDate =
      await this.authService.canViewMedicaOrderDate();
    const msg = this.getValidateDateMessage();
    if (canViewMedicaOrderDate && !notified && msg !== "") {
      notified = true;
      this.confirmDialogService
        .confirm("Reserva", msg, "Continuar de todas formas", "Validar")
        .subscribe(async (value) => {
          if (value) {
            await this.finalizeReservationAndGoReminder();
          }
        });
    } else {
      await this.finalizeReservationAndGoReminder();
    }
  }

  private async finalizeReservationAndGoReminder() {
    const reminders = await this.reservationService.finalizeReservation(
      this.reservation,
    );
    this.reservationFinished = true;
    this.reservationRescheduleWizardService.goReminder(
      reminders,
      this.reservation,
      this.returnNavigate,
    );
  }

  async sendResevationMail(): Promise<void> {
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((reminder) =>
      medicalAppointmentIds.push(reminder.MedicalAppointmentId),
    );
    return await this.reservationService.sendResevationMail(
      medicalAppointmentIds,
    );
  }

  async cancelByRescheduleMedicalAppointment() {
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((r) =>
      medicalAppointmentIds.push(r.MedicalAppointmentId),
    );
    const medicalAppointmentCancel = new MedicalAppointmentCancel();
    medicalAppointmentCancel.MedicalAppointmentIds = medicalAppointmentIds;
    medicalAppointmentCancel.IsReschedule = true;
    medicalAppointmentCancel.Observations = "";
    await this.reservationService.cancelMedicalAppointment(
      medicalAppointmentCancel,
    );
  }

  async reschedule() {
    this.isRescheduling = true;
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((r) =>
      medicalAppointmentIds.push(r.MedicalAppointmentId),
    );
    await this.cancelByRescheduleMedicalAppointment();
    this.navigateToReservationWizard();
  }

  async rescheduleDos(reservation: Reservation, returnNavigate: string) {
    this.returnNavigate = returnNavigate;
    this.isRescheduling = true;
    this.reservation = reservation;
  }

  async reAppointment(reservation: Reservation, returnNavigate: string) {
    this.returnNavigate = returnNavigate;
    this.reservation = reservation;
  }
}

@Injectable()
export class ReservationExtraordinaryWizardService extends ReservationWizardService {
  setSeeDraft(seeDraft: boolean): void {
    throw new Error("Method not implemented.");
  }

  setSeeNoRules(seeNoRules: boolean): void {
    throw new Error("Method not implemented.");
  }

  setSeeNoRulesRestriction(seeNoRules: boolean): void {
    throw new Error("Method not implemented.");
  }
  validationSlotMessage(): string {
    throw new Error("Method not implemented.");
  }
  isValidReservationSlots(): boolean {
    throw new Error("Method not implemented.");
  }
  clearReservationSlot(reservationSlot: ReservationSlot): void {
    throw new Error("Method not implemented.");
  }
  authorizationRequired(): boolean {
    throw new Error("Method not implemented.");
  }
  getReservationSlotToAuthorize(): ReservationSlot {
    throw new Error("Method not implemented.");
  }

  validateSlotReservation(): void {
    throw new Error("Method not implemented.");
  }

  setReservationExtraordinaryCalendar(
    reservationExtraordinaryCalendar: ReservationExtraordinaryCalendar,
  ) {
    this.reservationExtraordinaryCalendar = reservationExtraordinaryCalendar;
  }

  durationExtraordinaryReservation(
    equipmentId: number,
    physicianId: number,
  ): Promise<number> {
    return this.reservationService.durationExtraordinaryReservation(
      this.reservation.ServiceReservations[0],
      equipmentId,
      physicianId,
    );
  }

  getPatient(): PatientSearch {
    return this.reservation.Patient;
  }

  setReservationDate(date: Date) {
    this.reservation.SelectedDate = date;
  }

  setReservationSlot(reservationSlot: ReservationSlot): void {
    const serviceReservation = this.getAllServiceReservations.find(
      (s) =>
        s.PracticesRegionReservation[0].ServiceId === reservationSlot.ServiceId,
    );
    serviceReservation.ReservationSlot = reservationSlot;
  }

  selectedSlotCount(): number {
    return this.getAllServiceReservations.filter(
      (s) => s.ReservationSlot != null,
    ).length;
  }

  clearAllReservationSlot(): void {
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      item.ReservationSlot = null;
    });
  }

  existsReservationServiceWithOutSlot(): boolean {
    return (
      this.getAllServiceReservations.length === 0 ||
      this.getAllServiceReservations.findIndex(
        (s) => s.ReservationSlot == null,
      ) > -1
    );
  }

  removeServiceReservation(serviceReservation: ServiceReservation): void {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations.splice(index, 1);
  }

  getServiceReservation(index: number): ServiceReservation {
    return this.reservation.ServiceReservations.find((x) => x.Index === index);
  }

  getNewServiceReservation(): ServiceReservation {
    let plan: Plan;
    let entity: Entity;
    let center: Center;

    let serviceReservation: ServiceReservation = null;
    let index = 0;

    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      plan = item.Plan;
      entity = item.Entity;
      center = item.Center;

      if (item.Index > index) {
        index = item.Index;
      }

      if (
        serviceReservation == null &&
        item.principalPracticeRegionReservationName() === ""
      ) {
        serviceReservation = item;
      }
    });

    if (serviceReservation == null) {
      serviceReservation = new ServiceReservation();
      serviceReservation.Entity = entity;
      serviceReservation.Plan = plan;
      serviceReservation.Center = center;
      serviceReservation.Index = index + 1;
      this.reservation.ServiceReservations.push(serviceReservation);
    }

    return serviceReservation;
  }

  async getPracticesRegionSearchReservation(
    planId: number,
    excludedPracticesRegionIds: number[],
    serviceId?: number,
    centerId?: number,
  ): Promise<PracticeRegionSearchReservation[]> {
    const response: PracticeRegionSearchReservation[] = [];
    let items: PracticeRegionSearchReservation[] = [];

    if (!this.planPracticesRegionSearch.ContainsKey(planId.toString())) {
      this.planPracticesRegionSearch.Add(
        planId.toString(),
        await this.practiceService.getPracticesRegionSearchReservation(
          planId,
          null,
          centerId,
        ),
      );
    }

    items = this.planPracticesRegionSearch.Item(planId.toString());
    items.forEach((item: PracticeRegionSearchReservation) => {
      if (
        (serviceId == null || serviceId === item.ServiceId) &&
        excludedPracticesRegionIds.indexOf(item.PracticeRegionId) === -1
      ) {
        response.push(item);
      }
    });

    return response;
  }

  getPracticeRegionReservation(
    planId: number,
    name: string,
    excludePracticesIds: number[],
    principalPracticeRegionId?: number,
    centerId?: number,
  ): Observable<PracticeRegionReservation[]> {
    return this.practiceService.getPracticeRegionReservation(
      planId,
      name,
      excludePracticesIds,
      principalPracticeRegionId,
      centerId,
    );
  }

  clearQuestionsAndSlotReservation(): void {
    if (!this.reservationQuestion) {
      this.reservationQuestion = new ReservationQuestion();
    }
    this.reservationQuestion.QuestionsFinish = false;
    this.reservation.Answers = [];
    this.clearAllReservationSlot();
  }

  get isQuestionVisible(): boolean {
    return (
      this.reservationQuestion && this.reservationQuestion.Questions.length > 0
    );
  }

  get getAllPrincipalPracticeRegion(): PracticeRegion[] {
    const practicesRegion: PracticeRegion[] = [];
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      practicesRegion.push(item.PracticesRegionReservation[0].PracticeRegion);
    });
    return practicesRegion;
  }

  get getAllServiceReservations(): ServiceReservation[] {
    return this.reservation.getAllServiceReservations();
  }

  get allPracticesIds(): number[] {
    const ids: number[] = [];
    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      const allPracticesRegionIds = item.allPracticesRegionIds();
      if (allPracticesRegionIds.length > 0) {
        allPracticesRegionIds.forEach((id: number) => {
          ids.push(id);
        });
      }
    });
    return ids;
  }

  get allPrincipalPracticesIds(): number[] {
    const ids: number[] = [];
    this.getAllServiceReservations.forEach((item: ServiceReservation) => {
      ids.push(item.PracticesRegionReservation[0].Id);
    });
    return ids;
  }

  get allPracticesAndAditionalIds(): number[] {
    const ids: number[] = [];
    this.reservation.ServiceReservations.forEach((item: ServiceReservation) => {
      if (item.allPracticesAndAditionalRegionIds().length > 0) {
        item.allPracticesAndAditionalRegionIds().forEach((id: number) => {
          ids.push(id);
        });
      }
    });
    return ids;
  }

  async setServiceReservationPracticeInfo(
    serviceReservation: ServiceReservation,
  ) {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations[index].Center =
      serviceReservation.Center;
    this.reservation.ServiceReservations[index].Entity =
      serviceReservation.Entity;
    this.reservation.ServiceReservations[index].Plan = serviceReservation.Plan;
    this.reservation.ServiceReservations[index].PracticesRegionReservation =
      serviceReservation.PracticesRegionReservation;
    this.reservation.ServiceReservations[
      index
    ].PracticesRegionAditionalReservation =
      serviceReservation.PracticesRegionAditionalReservation;
    this.reservation.ServiceReservations[index].PracticeRegionReservation =
      serviceReservation.PracticeRegionReservation;
    this.reservation.ServiceReservations[index].SetSuppliesReservation =
      serviceReservation.SetSuppliesReservation;
  }

  async setServiceReservationOrderInfo(serviceReservation: ServiceReservation) {
    const index = this.reservation.ServiceReservations.findIndex(
      (x) => x.Index === serviceReservation.Index,
    );
    this.reservation.ServiceReservations[index].DateOrder =
      serviceReservation.DateOrder;
    this.reservation.ServiceReservations[index].DateAuthorization =
      serviceReservation.DateAuthorization;
    this.reservation.ServiceReservations[index].Diagnostic =
      serviceReservation.Diagnostic;
    this.reservation.ServiceReservations[index].ReferringPhysician =
      serviceReservation.ReferringPhysician;
    this.reservation.ServiceReservations[index].Observation =
      serviceReservation.Observation;
    this.reservation.ServiceReservations[index].PatientObservation =
      serviceReservation.PatientObservation;
    this.reservation.ServiceReservations[index].CredentialNumber =
      serviceReservation.CredentialNumber;
    this.reservation.ServiceReservations[index].SecurityCodeCredentialNumber =
      serviceReservation.SecurityCodeCredentialNumber;
  }

  async practicePriceReservation(): Promise<PracticePriceReservationResponse> {
    return await this.valorizationService.practicePriceReservation(
      this.reservation,
    );
  }

  async loadReservationQuestions() {
    if (this.allPracticesAndAditionalIds.length > 0) {
      this.reservationQuestion = new ReservationQuestion();
      const patientId = this.getPatient() ? this.getPatient().Id : null;
      const info = await this.reservationService.getReservationQuestions(
        this.allPracticesAndAditionalIds,
        patientId,
      );
      this.reservationQuestion.Answers = info.Answers;
      this.reservationQuestion.Questions = info.Questions;
    }
  }

  async getEntities(): Promise<Entity[]> {
    return await this.entityService.getEntities(true);
  }

  async getPlansByEntityId(entityId: number): Promise<Plan[]> {
    return await this.entityService.getPlansByEntityId(entityId);
  }

  async getAditionalPracticeAndSetSuppliesReservation(
    practiceRegionIds: number[],
    planId: number,
    centerId?: number,
  ): Promise<SetSuppliesAditionalReservation[]> {
    return await this.practiceService.getAditionalPracticeAndSetSuppliesReservation(
      practiceRegionIds,
      planId,
      centerId,
    );
  }

  async reservationCalendar(): Promise<ReservationCalendar> {
    return await this.reservationService.reservationRisCalendar(
      this.reservation,
    );
  }

  async completePatientInfo(reservationSlots: ReservationSlot[]) {
    const completeDateSlots =
      await this.reservationService.completePatientInfo(reservationSlots);
    reservationSlots.forEach((slot) => {
      const completeSlot = completeDateSlots.find(
        (cslot) => cslot.MedicalAppointmentId === slot.MedicalAppointmentId,
      );
      if (completeSlot) {
        slot.Patient =
          completeSlot.PatientLastName + ", " + completeSlot.PatientFirstName;
        slot.PatientLastName = completeSlot.PatientLastName;
        slot.PatientFirstName = completeSlot.PatientFirstName;
        slot.PracticeRegionId = completeSlot.PracticeRegionId;
        slot.PracticeRegionName = completeSlot.PracticeRegionName;
        slot.PatientId = completeSlot.PatientId;
      }
    });
  }

  async finalizeReservation() {

    if (this.reservationHisId) {
      await this.reservationService.finalizeReservationHis(this.reservationHisId);
    }

    const medicalAppointmentId =
      await this.reservationService.finalizeExtraordinaryReservation(
        this.reservation,
        this.reservationExtraordinaryCalendar,
      );
    this.reservationFinished = true;
    this.router.navigate(["admission-reception-wizard", medicalAppointmentId]);


  }

  async sendResevationMail(): Promise<void> {
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((reminder) =>
      medicalAppointmentIds.push(reminder.MedicalAppointmentId),
    );
    return await this.reservationService.sendResevationMail(
      medicalAppointmentIds,
    );
  }

  async cancelByRescheduleMedicalAppointment() {
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((r) =>
      medicalAppointmentIds.push(r.MedicalAppointmentId),
    );
    const medicalAppointmentCancel = new MedicalAppointmentCancel();
    medicalAppointmentCancel.MedicalAppointmentIds = medicalAppointmentIds;
    medicalAppointmentCancel.IsReschedule = true;
    medicalAppointmentCancel.Observations = "";
    await this.reservationService.cancelMedicalAppointment(
      medicalAppointmentCancel,
    );
  }

  async reschedule() {
    this.isRescheduling = true;
    const medicalAppointmentIds: number[] = [];
    this.reminders.forEach((r) =>
      medicalAppointmentIds.push(r.MedicalAppointmentId),
    );
    await this.cancelByRescheduleMedicalAppointment();
    this.navigateToReservationWizard();
  }

  async rescheduleDos(reservation: Reservation, returnNavigate: string) {
    this.returnNavigate = returnNavigate;
    this.isRescheduling = true;
    this.reservation = reservation;
  }

  async reAppointment(reservation: Reservation, returnNavigate: string) {
    this.returnNavigate = returnNavigate;
    this.reservation = reservation;
  }

  EnableCombo(): boolean {
    return false;
  }
}

@Injectable()
export class ReservationCTWizardService extends ReservationRegularWizardService {
  async reservationCalendar(): Promise<ReservationCalendar> {
    return await this.reservationService.reservationCTCalendar(
      this.reservation,
    );
  }
}

@Injectable()
export class ReservationPreWizardService extends ReservationExtraordinaryWizardService {
  async finalizeReservation() {
    const medicalAppointmentId =
      await this.reservationService.finalizeExtraordinaryReservation(
        this.reservation,
        this.reservationExtraordinaryCalendar,
      );
    this.reservationFinished = true;
    this.router.navigate([
      "admission-pre-reception-wizard",
      medicalAppointmentId,
    ]);
  }
}
